import React, { useEffect, useState, useMemo } from 'react';
import { Tabs, theme, Modal } from 'antd';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  useSensor,
  DragEndEvent
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/css';
import { RootState } from 'store/configureStore';
import i18n from 'constants/translation/i18n';
import AuditFindingsTable from 'components/Tables/AuditFindingsTable';
import AuditFindingsLayout from '../../../components/Layouts/DataManagementLayout/AuditFindingsLayout';
import * as Styled from './AuditFindingsMapping.styled';

// Extend Window interface for unsaved changes detection
declare global {
  interface Window {
    hasUnsavedChanges?: boolean;
    showUnsavedChangesModal?: () => Promise<boolean>;
  }
}

const { useToken } = theme;

const DraggableTabNode = ({
  className,
  onActiveBarTransform,
  ...props
}: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isSorting
  } = useSortable({
    id: props['data-node-key']
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move'
  };

  useEffect(() => {
    if (!isSorting) {
      onActiveBarTransform('');
    } else if (className?.includes('ant-tabs-tab-active')) {
      onActiveBarTransform(
        css`
          .ant-tabs-ink-bar {
            transform: ${CSS.Transform.toString(transform)};
            transition: ${transition} !important;
          }
        `
      );
    }
  }, [className, isSorting, transform, transition, onActiveBarTransform]);

  return React.cloneElement(props.children, {
    ref: setNodeRef,
    style,
    ...attributes,
    ...listeners
  });
};

const AuditFindingsMapping: React.FC = () => {
  const { token } = useToken();
  const location = useLocation();
  const { lang } = useSelector((state: RootState) => ({
    lang: state.global.lang
  }));

  const [activeKey, setActiveKey] = useState('1');
  const [className, setClassName] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);

  const [items, setItems] = useState([
    {
      key: '1',
      label: i18n.t('AUDIT_FINDINGS_MAPPING'),
      children: <AuditFindingsTable />
    }
  ]);

  // Track initial state for unsaved changes detection
  const [initialItems, setInitialItems] = useState(items);
  const [initialActiveKey, setInitialActiveKey] = useState('1');

  // Check if there are unsaved changes (for this component, it's mainly tab changes)
  const hasUnsavedChanges = useMemo(() => {
    // Compare items without children property to avoid circular references
    const itemsWithoutChildren = items.map(({ children, ...rest }) => rest);
    const initialItemsWithoutChildren = initialItems.map(
      ({ children, ...rest }) => rest
    );
    const itemsChanged =
      JSON.stringify(itemsWithoutChildren) !==
      JSON.stringify(initialItemsWithoutChildren);
    const activeKeyChanged = activeKey !== initialActiveKey;
    return itemsChanged || activeKeyChanged;
  }, [items, initialItems, activeKey, initialActiveKey]);

  // Store navigation guard in global state
  useEffect(() => {
    if (hasUnsavedChanges) {
      window.hasUnsavedChanges = true;
      window.showUnsavedChangesModal = () => {
        return new Promise((resolve) => {
          Modal.confirm({
            title: 'Unsaved Changes',
            content:
              'You have unsaved changes. If you leave now, they will be lost. Do you want to continue?',
            okText: 'Yes',
            cancelText: 'No',
            okType: 'danger',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
      };
    } else {
      window.hasUnsavedChanges = false;
      window.showUnsavedChangesModal = undefined;
    }

    return () => {
      window.hasUnsavedChanges = false;
      window.showUnsavedChangesModal = undefined;
    };
  }, [hasUnsavedChanges]);

  // Set initial values when data loads
  useEffect(() => {
    setInitialItems(items);
    setInitialActiveKey(activeKey);
  }, []);

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 }
  });

  useEffect(() => {
    i18n.changeLanguage(lang).then(() => {
      setItems([
        {
          key: '1',
          label: i18n.t('AUDIT_FINDINGS_MAPPING'),
          children: <AuditFindingsTable />
        }
      ]);
      setForceUpdate((prev) => prev + 1);
    });
  }, [lang]);

  return (
    <AuditFindingsLayout>
      <Styled.ContentWrapper token={token}>
        <Tabs
          className={className}
          activeKey={activeKey}
          items={items}
          onChange={setActiveKey}
          key={`${lang}-${forceUpdate}`}
          renderTabBar={(tabBarProps, DefaultTabBar) => (
            <DndContext
              sensors={[sensor]}
              onDragEnd={({ active, over }: DragEndEvent) => {
                if (!over || active.id === over.id) return;
                setItems((prev) => {
                  const oldIndex = prev.findIndex((i) => i.key === active.id);
                  const newIndex = prev.findIndex((i) => i.key === over.id);
                  const updated = [...prev];
                  const [moved] = updated.splice(oldIndex, 1);
                  updated.splice(newIndex, 0, moved);
                  return updated;
                });
              }}
            >
              <SortableContext
                items={items.map((i) => i.key)}
                strategy={horizontalListSortingStrategy}
              >
                <DefaultTabBar {...tabBarProps}>
                  {(node) => (
                    <DraggableTabNode
                      {...node.props}
                      key={node.key}
                      onActiveBarTransform={setClassName}
                    >
                      {node}
                    </DraggableTabNode>
                  )}
                </DefaultTabBar>
              </SortableContext>
            </DndContext>
          )}
        />
      </Styled.ContentWrapper>
    </AuditFindingsLayout>
  );
};

export default AuditFindingsMapping;
